/** Remembered Live/Paper view for the Trade Log. During a paper-trading
 * phase (e.g. supertrend's 6-month test) the operator tracks the Paper
 * view daily — making them re-click it every visit guarantees a "why is
 * the log empty?" moment. localStorage because it's a per-browser view
 * preference, like the alert-sound choice. */

import type { ExecutionMode, Horizon } from "@/types/tradeLog";

const STORAGE_KEY = "nss.tradelog.mode";
const HORIZON_KEY = "nss.tradelog.horizon";

export function getSavedExecutionMode(): ExecutionMode {
  try {
    if (typeof localStorage === "undefined") return "LIVE";
    return localStorage.getItem(STORAGE_KEY) === "PAPER" ? "PAPER" : "LIVE";
  } catch {
    return "LIVE"; // localStorage blocked (private mode) — safe default
  }
}

export function saveExecutionMode(mode: ExecutionMode): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // Best-effort — losing the preference is harmless.
  }
}

/** Fire `onChange` when ANOTHER tab changes the remembered Live/Paper view.
 *
 * The report pages open in their own browser tab, so the Trade Log and a
 * report are routinely on screen at once reading one localStorage key. A
 * `storage` event fires only in the tabs that did NOT write it, which is
 * exactly the set that needs to catch up — without this they disagree
 * until one is reloaded, and two views of the same book showing different
 * numbers reads as a data bug.
 *
 * Returns an unsubscribe function. */
export function subscribeExecutionMode(onChange: (mode: ExecutionMode) => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  const handler = (e: StorageEvent): void => {
    if (e.key !== STORAGE_KEY) return;
    onChange(e.newValue === "PAPER" ? "PAPER" : "LIVE");
  };
  window.addEventListener("storage", handler);
  return () => {
    window.removeEventListener("storage", handler);
  };
}

/** Remembered Intraday/Positional view (operator, 2026-08-10: the two
 * horizons read as different books and should not share a table). */
export function getSavedHorizon(): Horizon {
  try {
    if (typeof localStorage === "undefined") return "INTRADAY";
    return localStorage.getItem(HORIZON_KEY) === "POSITIONAL" ? "POSITIONAL" : "INTRADAY";
  } catch {
    return "INTRADAY";
  }
}

export function saveHorizon(horizon: Horizon): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(HORIZON_KEY, horizon);
  } catch {
    // Best-effort — losing the preference is harmless.
  }
}
